import { Component, Input, Output, EventEmitter, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { UserTypeBadgeService } from '../../../../core/services/user-type-badge.service';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';

import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { AppButton } from '../../../../shared/components/app-button/app-button';

@Component({
  selector: 'app-access-profile-users-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, DialogModule, ConfirmDialogModule, AppButton],
  providers: [ConfirmationService],
  templateUrl: './access-profile-users-modal.html',
})
export class AccessProfileUsersModal implements OnInit {
  @Input() profileId!: number;
  @Input() branchId!: number | null;
  @Input() profileName!: string;
  @Output() close = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);
  userTypeBadge = inject(UserTypeBadgeService);

  users: any[] = [];
  loading = false;

  ngOnInit() {
    this.loadUsers();
  }

  async loadUsers() {
    if (!this.profileId || !this.branchId) return;
    this.loading = true;
    try {
      this.users = await firstValueFrom(this.settingsService.getUsersForProfile(this.profileId, this.branchId));
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  confirmRemoveUser(user: any) {
    if (!this.branchId) return;
    const userId = user.user_id ?? user.id;
    if (!userId) return;

    this.confirmationService.confirm({
      key: 'removeUserConfirm',
      message: this.translate.instant('settings_profile_delete_user_confirmation'),
      header: this.translate.instant('settings_profile_delete_user'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('delete'),
      rejectLabel: this.translate.instant('cancel'),
      accept: () => this.removeUser(userId),
    });
  }

  private async removeUser(userId: number) {
    if (!this.branchId) return;
    try {
      // Clear the user's assignment in user_profile_tb (the source of truth).
      await firstValueFrom(this.settingsService.removeUserFromProfile(userId, this.branchId));
      toast.success(this.translate.instant('settings_profile_success_update'));
      this.loadUsers(); // Reload to refresh list
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('settings_profile_error_update'));
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .trim()
      .split(' ')
      .slice(0, 2)
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }
}
